import { Button } from "@material-ui/core";
import DialogContentText from "@material-ui/core/DialogContentText";
import { categoryUrl } from "@saleor/categories/urls";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignCategoriesDialog from "@saleor/components/AssignCategoryDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import CoverPage, {
  CoverDetailsPageTab,
  CoverPageFormData
} from "@saleor/cover/components/CoverPage";
import {
  useCoverCataloguesAddMutation,
  useCoverCataloguesRemoveMutation,
  useCoverDeleteMutation,
  useCoverUpdateMutation
} from "@saleor/cover/mutations";
import { useCoverDetailsQuery } from "@saleor/cover/queries";
import {
  coverListUrl,
  coverUrl,
  CoverUrlDialog,
  CoverUrlQueryParams
} from "@saleor/cover/urls";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { productUrl } from "@saleor/products/urls";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useProductSearch from "@saleor/searches/useProductSearch";
import { CoverCoverType, CoverTypeEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface CoverDetailsProps {
  id: string;
  params: CoverUrlQueryParams;
}

export function CoverValueTypeEnum(type: CoverCoverType): CoverTypeEnum {
  return type.toString() === CoverTypeEnum.FOUR_MAP_MASK
    ? CoverTypeEnum.FOUR_MAP_MASK
    : CoverTypeEnum.DETAILS;
}

const CoverDetails: React.FC<CoverDetailsProps> = ({ id, params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const intl = useIntl();

  const [openModal, closeModal] = createDialogActionHandlers<
    CoverUrlDialog,
    CoverUrlQueryParams
  >(navigate, params => coverUrl(id, params), params);

  const paginationState = createPaginationState(PAGINATE_BY, params);

  const { data, loading } = useCoverDetailsQuery({
    variables: {
      id,
      ...paginationState
    }
  });

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const [coverDelete, coverDeleteOpts] = useCoverDeleteMutation({
    onCompleted: data => {
      if (data.coverDelete?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "cover delete"
          })
        });
        navigate(coverListUrl());
      }
    }
  });

  const [coverUpdate, coverUpdateOpts] = useCoverUpdateMutation({
    onCompleted: data => {
      if (data.coverUpdate?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const handleUpdate = async (data: CoverPageFormData) => {
    const input = {
      ...data,
      coverType: CoverValueTypeEnum(data.coverType)
    };

    const result = await coverUpdate({
      variables: {
        id,
        input
      }
    });

    return result.data.coverUpdate.errors;
  };

  const tabPageInfo =
    params.activeTab === CoverDetailsPageTab.categories
      ? maybe(() => data.cover.categories.pageInfo)
      : maybe(() => data.cover.products.pageInfo);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    tabPageInfo,
    paginationState,
    params
  );

  const changeTab = (tab: CoverDetailsPageTab) => {
    reset();
    navigate(
      coverUrl(id, {
        activeTab: tab
      })
    );
  };

  const [
    coverCataloguesAdd,
    coverCataloguesAddOpts
  ] = useCoverCataloguesAddMutation({
    onCompleted: data => {
      if (data.coverCataloguesAdd?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Cover Unassign to Category"
          })
        });
      }
    }
  });
  const [
    onCataloguesUnassign,
    onCataloguesUnassignOpts
  ] = useCoverCataloguesRemoveMutation({
    onCompleted: data => {
      if (data.coverCataloguesRemove?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Cover Unassign to Category"
          })
        });
      }
    }
  });

  return (
    <>
      <CoverPage
        errors={coverUpdateOpts.data?.coverUpdate.errors || []}
        cover={maybe(() => data.cover)}
        disabled={loading}
        onBack={() => navigate(coverListUrl())}
        saveButtonBarState={coverUpdateOpts.status}
        onDelete={() => openModal("remove")}
        onSubmit={handleUpdate}
        isNew={false}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onCategoryAssign={() => openModal("assign-category")}
        pageInfo={pageInfo}
        onCategoryClick={id => () => navigate(categoryUrl(id))}
        onProductAssign={() => openModal("assign-product")}
        onProductUnassign={productId =>
          onCataloguesUnassign({
            variables: {
              ...paginationState,
              id,
              input: {
                products: [productId]
              }
            }
          })
        }
        onCategoryUnassign={categoryId =>
          onCataloguesUnassign({
            variables: {
              ...paginationState,
              id,
              input: {
                categories: [categoryId]
              }
            }
          })
        }
        onProductClick={id => () => navigate(productUrl(id))}
        activeTab={params.activeTab}
        onTabClick={changeTab}
        onRemove={() => openModal("remove")}
        categoryListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-category", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign category from cover, button"
              id="coverDetailsUnassignCategory"
            />
          </Button>
        }
        productListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-product", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign product from cover, button"
              id="coverDetailsUnassignProduct"
            />
          </Button>
        }
        isChecked={isSelected}
        selected={listElements?.length}
        toggle={toggle}
        toggleAll={toggleAll}
      ></CoverPage>

      <ActionDialog
        open={params.action === "remove"}
        confirmButtonState={coverDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() => coverDelete({ variables: { id } })}
        title={intl.formatMessage({
          defaultMessage: "Delete cover",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete this plugin({cover})?"
            description="dialog content"
            values={{
              cover: <strong>{data?.cover?.name}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        open={params.action === "unassign-category"}
        confirmButtonState={onCataloguesUnassignOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          onCataloguesUnassign({
            variables: {
              ...paginationState,
              id,
              input: {
                categories: params.ids
              }
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Unassign Categories From Cover",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to unassign category({number})?"
            description="dialog content"
            values={{
              number: <strong>{params?.ids?.length}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        open={params.action === "unassign-product"}
        confirmButtonState={onCataloguesUnassignOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          onCataloguesUnassign({
            variables: {
              ...paginationState,
              id,
              input: {
                products: params.ids
              }
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Unassign Products From Cover",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to unassign products({number})?"
            description="dialog content"
            values={{
              number: <strong>{params?.ids?.length}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <AssignCategoriesDialog
        categories={maybe(() =>
          searchCategoriesOpts.data.search.edges
            .map(edge => edge.node)
            .filter(suggestedCategory => suggestedCategory.id)
        )}
        confirmButtonState={coverCataloguesAddOpts.status}
        hasMore={searchCategoriesOpts.data?.search.pageInfo.hasNextPage}
        open={params.action === "assign-category"}
        onFetch={searchCategories}
        onFetchMore={loadMoreCategories}
        loading={searchCategoriesOpts.loading}
        onClose={closeModal}
        onSubmit={categories =>
          coverCataloguesAdd({
            variables: {
              ...paginationState,
              id,
              input: {
                categories
              }
            }
          })
        }
      />
      <AssignProductDialog
        confirmButtonState={coverCataloguesAddOpts.status}
        hasMore={searchProductsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "assign-product"}
        onFetch={searchProducts}
        onFetchMore={loadMoreProducts}
        loading={searchProductsOpts.loading}
        onClose={closeModal}
        onSubmit={products =>
          coverCataloguesAdd({
            variables: {
              ...paginationState,
              id,
              input: {
                products: products.map(product => product.id)
              }
            }
          })
        }
        products={mapEdgesToItems(searchProductsOpts?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
    </>
  );
};

export default CoverDetails;
